const SiteNav = class extends HTMLElement { // eslint-disable-line
  constructor() {
    super()
    this.menuBar = this.querySelector('ul')
    this.menuItems = this.menuBar.querySelectorAll(':scope > li[tabindex="0"], :scope > li > a, :scope > li > button')
    this.dropdownWrappers = this.querySelectorAll('[data-dropdown-wrapper]')
    this.scope = null
    this.tabbableElements = null
    this.menuOpen = false
    this.userIsTabbing = false

    // Set to true if it is needed for dropdown titles to also be clickable as a link.
    this.allowDropdownTriggerLinks = true
    this.desktopBreakpoint = 1800
  }

  connectedCallback() {
    this.initDropdowns()
    this.initKeybinds()
  }

  initDropdowns() {
    this.dropdownWrappers.forEach(dropdownWrapper => {
      const trigger = dropdownWrapper.querySelector('[data-dropdown-open]')

      trigger?.addEventListener('click', (event) => {
        if (window.innerWidth >= this.desktopBreakpoint && this.allowDropdownTriggerLinks) { return }
        event.preventDefault()
        this.toggleDropdown(dropdownWrapper)
      })
    })
  }

  openDropdown(wrapperEl) {
    const trigger = wrapperEl.querySelector('[data-dropdown-open]')
    const dropdown = wrapperEl.querySelector('[data-dropdown]')

    wrapperEl.classList.add('active')
    trigger?.setAttribute('aria-expanded', 'true')
    dropdown?.setAttribute('aria-hidden', 'false')
    this.menuOpen = true
  }

  toggleDropdown(wrapperEl) {
    const trigger = wrapperEl.querySelector('[data-dropdown-open]')
    const dropdown = wrapperEl.querySelector('[data-dropdown]')

    wrapperEl.classList.toggle('active')
    const isActive = wrapperEl.classList.contains('active')
    trigger?.setAttribute('aria-expanded', isActive)
    dropdown?.setAttribute('aria-hidden', !isActive)
    this.menuOpen = isActive
  }

  closeAllDropdowns() {
    this.dropdownWrappers.forEach(dropdownWrapper => {
      const trigger = dropdownWrapper.querySelector('[data-dropdown-open]')
      const dropdown = dropdownWrapper.querySelector('[data-dropdown]')

      dropdownWrapper.classList.remove('active')
      trigger?.setAttribute('aria-expanded', 'false')
      dropdown?.setAttribute('aria-hidden', 'true')
    })
  }

  initKeybinds() {
    document.body.addEventListener('focusin', () => {
      const activeElement = document.activeElement

      // Not in the nav, do nothing
      if (!activeElement.closest('site-nav') && !this.userInNav) { return }

      // Leaving or moving in the nav, let's rest the dropdowns
      this.closeAllDropdowns()

      // Leaving the nav, turn off some settings
      if (!activeElement.closest('site-nav') && this.userInNav) {
        this.menuOpen = false
        this.userInNav = false
        return
      }

      // We are in the nav. If there should be an open dropdown, open it
      const dropdownWrapper = activeElement.closest('[data-dropdown-wrapper]')
      if (dropdownWrapper && this.menuOpen) {
        this.openDropdown(dropdownWrapper)
      }

      // If we just entered the nav, scope the top level menu items
      if (!this.userInNav) {
        this.setScope(this)
      }
      this.userInNav = true
    })

    document.addEventListener('keydown', (event) => {
      if (!this.userInNav || event.metaKey || event.shiftKey || event.ctrlKey) { return }
      if (!this.userIsTabbing && event.code === 'Tab') {
        this.userIsTabbing = true
      }

      if (!this.scope) { return false }

      // If a key between A and Z is pressed, select the first or next element where the text corresponds to this letter.
      if (event.keyCode >= 65 && event.keyCode <= 90) {
        this.focusChar(event)
        return
      }

      const dropdownWrapper = event.target.closest('[data-dropdown-wrapper]')

      // Make the space key work like enter on nav
      if (event.code === 'Space') {
        if (event.target.hasAttribute('data-nav-link')) {
          this.openDropdown(dropdownWrapper)
          this.setScope(dropdownWrapper.querySelector('[data-dropdown]'))
          this.focusEnd(event, 'first')
        } else {
          event.preventDefault()
          event.target.click()
        }
        return
      }

      switch (event.key) {
        case 'Escape':
          this.closeAllDropdowns()
          this.menuOpen = false
          break
        case 'ArrowUp':
          if (!dropdownWrapper) { return }

          if (event.target.hasAttribute('data-nav-link')) {
            this.openDropdown(dropdownWrapper)
            this.setScope(dropdownWrapper.querySelector('[data-dropdown]'))
            this.focusEnd(event, 'last')
          } else {
            this.focus(event, -1)
          }
          break
        case 'ArrowDown':
          if (!dropdownWrapper) { return }

          if (event.target.hasAttribute('data-nav-link')) {
            this.openDropdown(dropdownWrapper)
            this.setScope(dropdownWrapper.querySelector('[data-dropdown]'))
            this.focusEnd(event, 'first')
          } else {
            this.focus(event, 1)
          }
          break
        case 'ArrowRight':
          this.setScope(this)
          this.focus(event, 1, dropdownWrapper ? event.target.closest('[data-dropdown-wrapper]').querySelector('[data-nav-link]') : document.activeElement)
          break
        case 'ArrowLeft':
          this.setScope(this)
          this.focus(event, -1, dropdownWrapper ? event.target.closest('[data-dropdown-wrapper]').querySelector('[data-nav-link]') : document.activeElement)
          break
        case 'Home':
          this.focusEnd(event, 'first')
          break
        case 'End':
          this.focusEnd(event, 'last')
          break
      }
    })
  }

  // Set the current menu scope to focus items within
  setScope(element) {
    this.scope = element
    this.tabbableElements = element === null ? null : (element.tagName.toLowerCase() === 'site-nav' ? this.menuItems : element.querySelectorAll('a[href], button:not(.disabled)'))
    this.tabbableElements = [...this.tabbableElements].filter(el => !el.closest('.menu-item--mobile') && element.getAttribute('tabindex') !== '-1')
    this.tabbableElements[0].focus();
  }

  // Focus element in a direction, 1 for next, -1 for previous
  focus(event, dir, targetElement = document.activeElement, array = this.tabbableElements) {
    event.preventDefault()
    const count = array.length - 1
    let focusIndex = [...array].findIndex(child => child === targetElement) + dir
    focusIndex = focusIndex < 0 ? count : focusIndex
    focusIndex = focusIndex > count ? 0 : focusIndex

    array[focusIndex].focus()
  }

  // Focus first or last element
  focusEnd(event, pos) {
    event.preventDefault()
    const target = pos === 'first' ? 0 : this.tabbableElements.length - 1
    const targetElement = this.tabbableElements[target]

    setTimeout(() => {
      targetElement.focus()
    }, 100);
  }

  // Focus first or next where the text content's first letter matches the pressed key.
  focusChar(event) {
    event.preventDefault()
    // Find all elements where the first letter is the pressed key AND the active element
    const relevantArray = [...this.tabbableElements].filter(child => child.textContent.trim().toLowerCase().charAt(0) === event.key || child === document.activeElement)
    // Get current active element index
    const activeIndex = [...relevantArray].findIndex(child => child === document.activeElement)
    // Offset the array by the current active element. Allows using the focus function to select the next/prev
    const offsetArray = this.offsetArray(relevantArray, activeIndex)
    // Get only the relevant key items (removes active element if it does not match)
    const characterArray = [...offsetArray].filter(child => child.textContent.trim().toLowerCase().charAt(0) === event.key)
    if (!characterArray.length) { return }

    this.focus(event, 1, undefined, characterArray)
  }

  offsetArray(array, offset) {
    return [...[...array].slice(offset), ...[...array].slice(0, offset)]
  }
};

if (!window.customElements.get('site-nav')) {
  window.customElements.define('site-nav', SiteNav);
}